import classNames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import { FC, useCallback, useMemo } from 'react';
import type { ActionName } from 'state/common-form/infos/infos.model';
import { logFrontError } from 'utils/errors/logFrontError';
import { camelCaseToWords } from 'utils/string';

export interface ActionButtonLegacyProps {
  className?: string;
  name: ActionName;
  isDisabled: boolean;
  isNotReadyToQuote: boolean;
  onQuoteAction: (name: ActionName) => void;
}

function getActionNameTranslationKey(name: ActionName): IntlKey {
  switch (name) {
    case 'IndicativeQuote':
      return 'form.buttons.IndicativeQuote';
    case 'TradableQuote':
      return 'form.buttons.TradableQuote';
    case 'AdvancedTradableQuote':
      return 'form.buttons.AdvancedTradableQuote';
    case 'ExecutableQuote':
      return 'form.buttons.ExecutableQuote';
    case 'ExecOnly':
      return 'form.buttons.ExecOnly';
    case 'AddonQuote':
      return 'form.buttons.AddonQuote';
    case 'AddonQuoteExecutionOnly':
      return 'form.buttons.QuoteExecOnly';
    default: {
      const error = new Error(`Action button named '${name}' is not yet handled in front`);
      logFrontError('Failed to get translation key for side panel action button {@FrontError}', error);
      return 'form.buttons.Unknown';
    }
  }
}

export function isTradeAction(name: ActionName): boolean {
  return name === 'ExecOnly' || name === 'TradableQuote' || name === 'AdvancedTradableQuote';
}

export const ActionButtonLegacy: FC<ActionButtonLegacyProps> = ({
  className,
  name,
  isDisabled,
  isNotReadyToQuote,
  onQuoteAction,
}) => {
  const onClick = useCallback(() => onQuoteAction(name), [onQuoteAction, name]);
  const isTradable = useMemo(() => isTradeAction(name), [name]);
  const translationKey = useMemo(() => getActionNameTranslationKey(name), [name]);
  const isUnknownButton = useMemo(() => translationKey === 'form.buttons.Unknown', [translationKey]);
  const labelForUnknownButton = useMemo(() => camelCaseToWords(name), [name]);

  return (
    <button
      key={name}
      data-e2e={name + 'Button'}
      disabled={isDisabled || isUnknownButton}
      className={classNames('btn btn-xl', className, {
        'btn-secondary': isNotReadyToQuote && !isTradable,
        'btn-primary': isNotReadyToQuote && isTradable,
        'btn-success': !isNotReadyToQuote && !isTradable,
        'btn-info': !isNotReadyToQuote && isTradable,
      })}
      onClick={onClick}
    >
      {isUnknownButton ? (
        <span>{labelForUnknownButton}</span>
      ) : (
        <FormattedMessage id={translationKey} defaultMessage={name} />
      )}
    </button>
  );
};

ActionButtonLegacy.displayName = 'ActionButtonLegacy';
